/**
 * Tiny dependency-free confetti burst.
 *
 * Spawns a fixed, full-viewport <canvas> above everything, animates a
 * burst of paper particles, then removes itself once they settle. No
 * external library. Honors prefers-reduced-motion (no-ops). Multiple
 * bursts reuse a single canvas so overlapping calls don't stack DOM.
 */

type ConfettiOptions = {
  /** Bigger, denser burst for big moments (badge unlock). */
  big?: boolean;
  /** Vertical origin as a fraction of viewport height (0 top … 1 bottom). */
  originY?: number;
  /** Horizontal origin as a fraction of viewport width. */
  originX?: number;
};

const COLORS = ["#2563eb", "#10b981", "#f59e0b", "#8b5cf6", "#ef4444", "#06b6d4"];

type Particle = {
  x: number; y: number; vx: number; vy: number; g: number;
  s: number; rot: number; vr: number; col: string; life: number;
};

let canvas: HTMLCanvasElement | null = null;
let ctx: CanvasRenderingContext2D | null = null;
let particles: Particle[] = [];
let raf: number | null = null;

function ensureCanvas() {
  if (canvas) return;
  canvas = document.createElement("canvas");
  canvas.style.cssText =
    "position:fixed;inset:0;width:100%;height:100%;pointer-events:none;z-index:9999";
  document.body.appendChild(canvas);
  ctx = canvas.getContext("2d");
  resize();
  window.addEventListener("resize", resize);
}

function resize() {
  if (!canvas || !ctx) return;
  const dpr = window.devicePixelRatio || 1;
  canvas.width = window.innerWidth * dpr;
  canvas.height = window.innerHeight * dpr;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
}

function teardown() {
  if (raf) cancelAnimationFrame(raf);
  raf = null;
  window.removeEventListener("resize", resize);
  if (canvas && canvas.parentNode) canvas.parentNode.removeChild(canvas);
  canvas = null;
  ctx = null;
  particles = [];
}

function loop() {
  if (!ctx || !canvas) return;
  const w = window.innerWidth;
  const h = window.innerHeight;
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  for (let i = particles.length - 1; i >= 0; i--) {
    const p = particles[i];
    p.vy += p.g;
    p.x += p.vx;
    p.y += p.vy;
    p.rot += p.vr;
    p.life -= 0.008;
    if (p.life <= 0 || p.y > h + 20) {
      particles.splice(i, 1);
      continue;
    }
    ctx.save();
    ctx.globalAlpha = Math.max(0, p.life);
    ctx.translate(p.x, p.y);
    ctx.rotate(p.rot);
    ctx.fillStyle = p.col;
    ctx.fillRect(-p.s / 2, -p.s / 2, p.s, p.s * 0.6);
    ctx.restore();
  }
  if (particles.length) {
    raf = requestAnimationFrame(loop);
  } else {
    teardown();
  }
  // touch w to satisfy noUnusedLocals in some configs
  void w;
}

export function fireConfetti(options: ConfettiOptions = {}): void {
  if (
    typeof window === "undefined" ||
    (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches)
  ) {
    return;
  }
  ensureCanvas();
  const { big = false, originY = 0.4, originX = 0.5 } = options;
  const cx = window.innerWidth * originX;
  const cy = window.innerHeight * originY;
  const n = big ? 140 : 80;
  for (let i = 0; i < n; i++) {
    const ang = Math.random() * Math.PI * 2;
    const spd = 3 + Math.random() * (big ? 9 : 6);
    particles.push({
      x: cx,
      y: cy,
      vx: Math.cos(ang) * spd,
      vy: Math.sin(ang) * spd - 4,
      g: 0.16 + Math.random() * 0.1,
      s: 5 + Math.random() * 6,
      rot: Math.random() * Math.PI,
      vr: (Math.random() - 0.5) * 0.3,
      col: COLORS[(Math.random() * COLORS.length) | 0],
      life: 1,
    });
  }
  if (!raf) raf = requestAnimationFrame(loop);
}
